<template>
  <v-sheet class="px-3 d-flex flex-column fill-height">
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-row class="header-sticky flex-grow-1">
      <v-card :color="cardColor" min-width="640" class="w-100 d-flex flex-column flex-grow-1" :width="'100%'" elevation="0">
        <v-card-title>
          <v-row justify="start" no-gutters>
            <v-icon large class="pr-2">mdi-chart-timeline</v-icon>
            <h3>{{ $t("vacationReport") }}</h3>
          </v-row>
        </v-card-title>

        <v-card-text class="d-flex flex-column flex-grow-1">
          <v-row class="my-3" no-gutters>
            <v-btn class="mr-2" :color="getButtonSelectionState('detailed')" @click="setButtonSelection('detailed')">
              {{ $t("detailedReport") }}
            </v-btn>
            <v-btn class="mx-2" :color="getButtonSelectionState('perUser')" @click="setButtonSelection('perUser')">
              {{ $t("perUserReport") }}
            </v-btn>
            <v-btn class="mx-2" :color="getButtonSelectionState('perDepartment')" @click="setButtonSelection('perDepartment')">
              {{ $t("perDepartmentReport") }}
            </v-btn>

            <v-spacer />

            <v-row v-if="displayData.length > 0" class="pa-0 ma-0" align="center" justify="end" no-gutters>
              <v-tooltip bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon v-on="on" @click="resetFilterCache" class="pr-4"> mdi-arrow-u-left-top </v-icon>
                </template>
                <span>{{ $t("resetFilter") }}</span>
              </v-tooltip>

              <v-btn prepend-icon="mdi-filter-outline" @click="handleSelectionClick" class="primary" color="white--text">
                {{ $t("filterColumns") }}
              </v-btn>

              <v-divider class="mx-3" vertical />

              <div class="d-flex page-size-wrapper">
                <v-select class="page-size-select" :label="$t('pageSize')" :items="pageSizes" v-model="pageSize" outlined hide-details dense />
              </div>

              <v-divider class="mx-3" vertical />

              <v-tooltip v-if="displayData.length > 0" bottom color="primary" class="white-text">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" icon @click="handleCSVExport">
                    <v-icon large>mdi-file-export-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("exportBtn") }}</span>
              </v-tooltip>
            </v-row>
          </v-row>

          <v-divider class="my-4" />

          <v-row class="pa-0 ma-0 align-center">
            <v-col class="pl-1">
              <v-autocomplete
                v-model="selectedTeams"
                :items="teams"
                item-text="Team"
                color="primary"
                chips
                elevated
                prepend-icon="mdi-account-group"
                :label="$t('teams')"
                multiple
                hide-details
                dense
              >
                <template v-slot:selection="data">
                  <v-chip
                    v-if="data.index < 3"
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    close
                    @click="data.select"
                    @click:close="removeSelectedTeam(data.item)"
                  >
                    {{ data.item }}
                  </v-chip>
                  <span v-if="data.index === selectedTeams.length - 1 && selectedTeams.length > 3">
                    {{ ` (+${selectedTeams.length - 3})` }}
                  </span>
                </template>

                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title>{{ data.item }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="selectedEmployees"
                :items="filteredEmployees"
                item-text="name"
                color="primary"
                chips
                elevated
                :label="$t('employees')"
                multiple
                prepend-icon="mdi-account"
                hide-details
                dense
                return-object
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @mousedown.prevent @click="handleSelectAll" dense hide-details>
                    <v-list-item-action>
                      <v-icon :color="selectedEmployees.length > 0 ? 'red darken-4' : ''">
                        {{ selectedEmployees.length > 0 ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ $t("selectAll") }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2" />
                </template>

                <template v-slot:selection="data">
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on }">
                      <v-chip
                        v-on="on"
                        v-if="data.index < 1"
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click="data.select"
                        @click:close="removeSelectedEmployee(data.item)"
                      >
                        {{ data.item.name }}
                      </v-chip>
                    </template>
                    <span>{{ selectedEmployees.map((e) => e.name).join(", \n") }}</span>
                  </v-tooltip>
                  <span v-if="data.index === selectedEmployees.length - 1 && selectedEmployees.length > 1">
                    {{ ` (+${selectedEmployees.length - 1})` }}
                  </span>
                </template>

                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ setTeamsForAutocomplete(data.item.teams) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col style="display: inline-flex" class="mx-2">
              <v-icon>mdi-calendar-multiselect-outline</v-icon>
              <DatePicker v-model="date.from" dense :mydate1="date.from" :label="$t('dateFrom')" />
            </v-col>

            <v-col style="display: inline-flex" class="mx-2">
              <v-icon>mdi-calendar-multiselect-outline</v-icon>
              <DatePicker v-model="date.until" dense :mydate1="date.until" :label="$t('dateUntil')" />
            </v-col>

            <v-btn v-if="!loaderOverlay" class="ml-3 mr-1" color="primary" @click="refresh">
              {{ $t("refresh") }}
            </v-btn>
            <v-btn v-else class="mx-3" color="primary" icon>
              <v-icon class="rotate">mdi-refresh</v-icon>
            </v-btn>
          </v-row>

          <v-divider class="my-4" />

          <v-row class="pa-0 ma-0 flex-grow-1">
            <v-col class="pa-0 d-flex flex-column">
              <v-sheet v-if="displayData.length > 0" class="report-scroll" :style="{ height: listHeight + 'px', maxHeight: listHeight + 'px' }">
                <DynamicList
                  class="flex-grow-1"
                  :pageSize="pageSize"
                  :data="displayData"
                  :styles="styles"
                  :headers="headers"
                  :headerTypes="headerTypes"
                  :columnSelection="columnSelection"
                />
              </v-sheet>

              <v-card v-else-if="!loaderOverlay" class="mx-0 mt-2 py-5 flex-grow-1 d-flex align-center justify-center">
                <NoData />
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-row>

    <v-row class="wrapper mt-5" cols="2" style="position: fixed" :style="{ top: position.top, left: position.left }">
      <v-card v-if="selectionIsOpen" :color="$vuetify.theme.dark ? '#1E1E1E' : 'grey lighten-2'" width="unset" class="mr-10 pa-5">
        <ColumnControl
          v-click-outside="handleOutsideClick"
          id="column-control"
          :headers="headers"
          :columnSelection="columnSelection"
          @update="updateSelectedColumns"
        />
      </v-card>
    </v-row>

    <LoadingInfo :state="loadingInfo.state" :message="loadingInfo.message" />
  </v-sheet>
</template>

<script>
import DynamicList from "../../components/DynamicList/DynamicList.vue";
import ColumnControl from "../../components/DynamicList/ColumnControl.vue";
import DatePicker from "../../components/SimpleComponents/DatePicker.vue";
import NoData from "../../components/GeneralReporting/WorkTimeReport/NoData.vue";
import LoadingInfo from "../../components/Global/LoadingInfo.vue";
import headerTypes from "../../components/GeneralReporting/AbsenceReport/headerTypes.js";
import headerStyles from "../../components/GeneralReporting/AbsenceReport/headerStyles.js";
import { mapGetters } from "vuex";
import { HEADER_STYLES } from "../../enums/HeaderStyles.js";

export default {
  name: "AbsenceReport",
  route: "AbsenceReport",
  components: {
    DatePicker,
    DynamicList,
    ColumnControl,
    NoData,
    LoadingInfo
  },
  data: (vm) => ({
    loaderOverlay: false,
    loadingInfo: {
      state: false,
      message: ""
    },
    loadingAnimationIterations: 0,
    loadingInterval: null,
    pageSize: 10,
    pageSizes: [10, 25, 50],
    columnSelection: [],
    data: [],
    displayData: [],
    headerTypes: headerTypes,
    styles: headerStyles,
    optionalHeaders: ["U_factor", "U_extID", "teams"],
    position: {
      top: undefined,
      left: undefined
    },
    selectionIsOpen: false,
    selectedEmployees: [],
    selectedTeams: [],
    date: {
      from: vm.getDate(),
      until: vm.getDate()
    },
    currentView: "detailed",
    listHeight: 0
  }),
  async created() {
    if (localStorage.getItem("absenceReportFromDate") != null) this.date.from = localStorage.getItem("absenceReportFromDate");
    if (localStorage.getItem("absenceReportUntilDate") != null) this.date.until = localStorage.getItem("absenceReportUntilDate");
    if (localStorage.getItem("absenceReportEmployeesFilter") != null)
      this.selectedEmployees = JSON.parse(localStorage.getItem("absenceReportEmployeesFilter") ?? "[]");
    if (localStorage.getItem("absenceReportTeamsFilter") != null) this.selectedTeams = JSON.parse(localStorage.getItem("absenceReportTeamsFilter") ?? "[]");

    this.loadingInfo.state = true;
    this.loadingInfo.message = `${this.$t("fetchingData")}...`;

    await this.getUserList();
    await this.getTeams();
    await this.fetch();
    this.fetchAbsenceReasons();

    this.loadingInfo.message = "";
    this.loadingInfo.state = false;
  },
  mounted() {
    this.updateListHeight();
    window.addEventListener("resize", this.updateListHeight);
    this.$nextTick(() => {
      const drawer = document.querySelector(".v-navigation-drawer");
      if (drawer) drawer.addEventListener("transitionend", this.updateListHeight);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateListHeight);
    const drawer = document.querySelector(".v-navigation-drawer");
    if (drawer) drawer.removeEventListener("transitionend", this.updateListHeight);
  },
  watch: {
    currentView(val) {
      this.reProcessData(val);
    },
    loadingInfo: {
      handler(val) {
        if (this.loadingInterval) return;
        if (val.message.endsWith("...")) {
          this.animateLoadingInfo();
        } else {
          this.stopAnimation();
        }
      },
      deep: true
    },
    "date.from"(val) {
      localStorage.setItem("absenceReportFromDate", val);
    },
    "date.until"(val) {
      localStorage.setItem("absenceReportUntilDate", val);
    },
    selectedTeams(val) {
      localStorage.setItem("absenceReportTeamsFilter", JSON.stringify(val));
    },
    selectedEmployees(val) {
      localStorage.setItem("absenceReportEmployeesFilter", JSON.stringify(val));
    }
  },
  computed: {
    headers() {
      return [...new Set(this.displayData.map((entry) => Object.keys(entry)).flat())];
    },
    cardColor() {
      return this.$vuetify.theme.dark ? "#2E2E2E" : "#FFFFFF";
    },
    selectedUser() {
      if (this.isOneEmployee) {
        return this.employees.find((employee) => employee.SAPEmpID === this.selectedEmployees[0].SAPEmpID);
      } else {
        return this.selectedEmployees[0];
      }
    },
    isOneEmployee() {
      const employee = this.displayData[0]?.U_employeeId;
      return this.displayData.every((entry) => entry.U_employeeId === employee);
    },
    filteredEmployees() {
      if (this.selectedTeams.length === 0) return this.employees;
      if (this.selectedTeams.find((team) => team === this.$t("all"))) return this.employees;
      return this.employees.filter((employee) => {
        return this.selectedTeams.some((team) => employee.teams.find(({ Team }) => Team === team));
      });
    },
    ...mapGetters({
      teams: "users/teams",
      employees: "users/employeeList",
      absenceReasons: "absences/absenceReasons"
    })
  },
  methods: {
    updateListHeight() {
      const bp = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint : null;

      const viewportHeight = bp && bp.height ? bp.height : window.innerHeight || 900;
      const viewportWidth = bp && bp.width ? bp.width : window.innerWidth || 1280;

      let offset = 420;

      const drawer = document.querySelector(".v-navigation-drawer");
      const drawerWidth = drawer ? drawer.getBoundingClientRect().width : 0;
      const menuOpen = drawerWidth > 100;

      const contentWidth = viewportWidth - (menuOpen ? drawerWidth : 0);

      const needsExtraSpace = menuOpen && contentWidth < 1050;

      if (needsExtraSpace) {
        offset = 460;
      }

      this.listHeight = viewportHeight - offset;
    },
    handleSelectAll() {
      if (this.selectedEmployees.length === this.filteredEmployees.length) {
        this.selectedEmployees = [];
      } else {
        this.selectedEmployees = this.filteredEmployees;
      }
    },
    fetchAbsenceReasons() {
      this.$store.dispatch("absences/fetchAllAbsenceReasons").then((res) => {
        this.optionalHeaders.push(...res.map((reason) => reason.name));
        const existing = new Set(this.headerTypes.map((h) => h.column));
        res.forEach(({ name }) => {
          if (!existing.has(name)) {
            this.headerTypes.push({
              column: name,
              value: HEADER_STYLES.RANGE
            });
          }
        });
      });
    },
    animateLoadingInfo() {
      const interval = setInterval(() => {
        if (!this.loadingInfo.message) return this.stopAnimation();
        const parts = this.loadingInfo.message.split(".");
        let dots = "";
        for (let i = 0; i < this.loadingAnimationIterations % 4; i++) {
          dots += ".";
        }
        this.loadingInfo.message = parts[0] + dots;
        this.loadingAnimationIterations++;
      }, 500);
      this.loadingInterval = interval;
    },
    stopAnimation() {
      if (!this.loadingInterval) return;
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    },
    getFullTimeStamp() {
      return `${this.getDate()}_${this.getTime()}`;
    },
    downloadCSVFile(csvString) {
      const fileName = `COBI-Time_Reports_Absences_${this.date.from}-${this.date.until}.csv`;
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
    handleCSVExport() {
      const delimiter = ";";
      const filterData = [];
      for (const item of this.displayData) {
        const object = {};
        for (const [key, value] of Object.entries(item)) {
          if (this.columnSelection.find((column) => column === key) != null) object[key] = value;
        }
        filterData.push(object);
      }
      const selectedColumn = Object.keys(filterData[0]);
      const headers = selectedColumn.map((header) => this.$t(header));
      const stringifiedElements = filterData.map((element) =>
        Object.values(element)
          .map((elem) => {
            elem = elem.toString().replace(/\./g, ",");
            return elem[0] === "-" || elem.split("").includes(":") ? ` ${elem}` : elem;
          })
          .join(delimiter)
      );
      const CSVOutput = `${headers.join(delimiter)}\n${stringifiedElements.join("\n")}`;
      this.downloadCSVFile(CSVOutput);
    },
    async handleCurrentView(val) {
      switch (val) {
        case "perUser":
          this.displayData = await this.sumUpUserData();
          break;
        case "perDepartment":
          this.displayData = await this.sumUpDepartmentData();
          break;
        default:
          await this.reProcessLines();
          break;
      }
    },
    handleOutsideClick() {
      this.selectionIsOpen = false;
      this.position = { top: undefined, left: undefined };
    },
    reorderColumns(data, columns) {
      return data.map((entry) => {
        let intermediary = columns.reduce((obj, key) => {
          obj[key] = entry[key];
          return obj;
        }, {});
        intermediary = { ...intermediary, ...entry };
        return intermediary;
      });
    },
    updateSelectedColumns(update) {
      localStorage.setItem("absenceReportFilter", JSON.stringify(update));
      this.columnSelection = structuredClone(update);
    },
    updateColumnSelection(selection) {
      const newHeaders = selection.filter((header) => !this.optionalHeaders.includes(header));
      const headerFromLocalStore = JSON.parse(localStorage.getItem("absenceReportFilter") ?? "[]");
      if (headerFromLocalStore.length !== 0) this.columnSelection = headerFromLocalStore;
      else this.columnSelection = newHeaders;
    },
    translateFields(entry) {
      if (typeof entry.U_countAsWorkingTime === "string") return entry;
      entry.U_countAsWorkingTime = entry.U_countAsWorkingTime === 0 ? this.$t("paid") : this.$t("unpaid");
      return entry;
    },
    isSameYear() {
      return this.date.from.split("-")[0] === this.date.until.split("-")[0];
    },
    reProcessData(val) {
      val ??= this.currentView;
      switch (val) {
        case "detailed":
          this.displayData = this.prepareDetailedViewData();
          break;
        case "perUser":
          this.displayData = this.sumUpUserData();
          break;
        case "perDepartment":
          this.displayData = this.sumUpDepartmentData();
          break;
        default:
          break;
      }
      this.updateColumnSelection(this.headers);
    },
    async refresh() {
      this.loadingInfo.state = true;
      this.loadingInfo.message = `${this.$t("fetchingData")}...`;
      await this.fetch();
      this.reProcessData();
      this.loadingInfo.state = false;
      this.loadingInfo.message = "";
    },
    prepareDetailedViewData() {
      const keysToDelete = ["U_balancePrev", "U_balanceStart", "U_accountId", "U_sapEmpId", "U_absenceId", "U_usercode", "U_prevYearVacation"];
      const cleaned = this.data.map((entry) => {
        keysToDelete.forEach((key) => delete entry[key]);
        entry = this.translateFields(entry);
        return entry;
      });
      const priorityColumns = ["U_name", "U_extID", "teams", "U_date", "U_reason", "U_countAsWorkingTime"];
      const columns = [...priorityColumns, ...Object.keys(cleaned[0] ?? []).filter((key) => !priorityColumns.includes(key))];
      const reordered = this.reorderColumns(cleaned, columns);
      this.updateColumnSelection(columns);
      return reordered;
    },
    async fetch() {
      if (!this.isSameYear()) {
        return this.$eventHub.$emit("showError", this.$t("vacationReportNotSameYear"), "bottom", "right");
      }
      await Promise.all([this.getUserList(), this.getTeams()]);
      const res = await this.$store.dispatch("reporting/getVacationReportsForUsers", {
        employees: this.getSelectedEmployees().map((employee) => ({
          SAPEmpID: employee.SAPEmpID,
          name: employee.name,
          extID: employee.extID,
          teams: employee.teams
        })),
        dateFrom: this.date.from,
        dateTo: this.date.until
      });
      this.data = res.map((entry) => {
        delete entry.U_colorCode;
        delete entry.U_icon;
        delete entry.U_deleted;
        delete entry.U_requestable;
        return entry;
      });
      if (this.data.length === 0) {
        this.displayData = [];
        this.loaderOverlay = false;
        return;
      }
      this.displayData = this.prepareDetailedViewData();
    },
    sumUpUserData() {
      const { data } = this;
      const users = [...new Set(data.map((entry) => entry.U_name))];
      const newData = [];
      for (const user of users) {
        const linesForUser = data.filter((entry) => entry.U_name === user);
        const [head, ...tail] = linesForUser.sort((a, b) => b.U_usedVacation - a.U_usedVacation);
        const base = {
          U_name: user,
          U_extID: head.U_extID,
          teams: head.teams,
          U_usedVacation: head.U_usedVacation,
          U_openVacation: head.U_openVacation,
          U_totalVacation: head.U_totalVacation
        };
        const aggregatedAbsences = this.aggregateUserAbsences([head, ...tail]);
        const sum = {
          ...base,
          ...aggregatedAbsences
        };
        newData.push(sum);
      }
      return newData;
    },
    sumUpDepartmentData() {
      const userEntries = this.sumUpUserData();
      const departments = new Map();
      const reasonNames = this.absenceReasons.map((r) => r.name);

      for (const entry of userEntries) {
        const departmentNames = (entry.teams || []).map((t) => t.Team);
        for (const department of departmentNames) {
          if (!departments.has(department)) {
            const initialReasons = reasonNames.reduce((acc, name) => {
              acc[name] = 0;
              return acc;
            }, {});
            departments.set(department, {
              U_name: department,
              U_memberCount: 0,
              U_usedVacation: 0,
              U_openVacation: 0,
              U_totalVacation: 0,
              ...initialReasons
            });
          }
          const dep = departments.get(department);
          dep.U_memberCount += 1;
          dep.U_usedVacation += entry.U_usedVacation || 0;
          dep.U_openVacation += entry.U_openVacation || 0;
          dep.U_totalVacation += entry.U_totalVacation || 0;
          for (const reasonName of reasonNames) {
            dep[reasonName] += entry[reasonName] || 0;
          }
        }
      }

      return Array.from(departments.values());
    },
    aggregateUserAbsences(lines) {
      const userAbsences = new Map();
      for (const reason of this.absenceReasons) {
        const days = lines.filter((line) => line.U_reason === reason.name).length;
        if (reason.id === 2) {
        } else userAbsences.set(reason.name, days);
      }
      return this.objFromMap(userAbsences);
    },
    objFromMap(map) {
      return [...map.entries()].reduce((acc, [key, value]) => {
        return { ...acc, [key]: value };
      }, {});
    },
    handleSelectionClick(event) {
      this.selectionIsOpen = !this.selectionIsOpen;
      const clickLeft = event.clientX;
      const clickTop = event.clientY;
      const card = document.getElementById("column-control");
      const cardWidth = card?.style.width || 180;
      const newLeft = clickLeft - cardWidth;
      this.position.top = `${clickTop}px`;
      this.position.left = `${newLeft}px`;
    },
    getSelectedEmployees() {
      const employeesSelected = this.selectedEmployees.length > 0;
      const teamsSelected = this.selectedTeams.length > 0;
      switch (true) {
        case employeesSelected && teamsSelected:
          return this.selectedEmployees;
        case employeesSelected:
          return this.selectedEmployees;
        case teamsSelected:
          return this.filteredEmployees;
        default:
          return this.employees;
      }
    },
    async getTeams() {
      await this.$store.dispatch("users/getTeams");
    },
    async getUserList() {
      if (this.$store.getters["user/user"].userGroupRights.canAccessGeneralReporting) await this.$store.dispatch("users/getEmployees");
      else if (this.$store.getters["user/user"].userGroupRights.canAccessGeneralReportingTeam) await this.$store.dispatch("users/getEmployeesFromSameTeam");
    },
    getTime() {
      const date = new Date();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      return [hours, minutes, seconds].map((e) => e.toString().padStart(2, "0")).join(":");
    },
    getDate() {
      const date = new Date();
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return [year, month, day].map((e) => e.toString().padStart(2, "0")).join("-");
    },
    setTeamsForAutocomplete(teams) {
      return teams.map((team) => team.Team).join(", ");
    },
    removeSelectedEmployee(item) {
      const index = this.selectedEmployees.findIndex((employee) => employee.SAPEmpID === item.SAPEmpID);
      if (index >= 0) this.selectedEmployees.splice(index, 1);
    },
    removeSelectedTeam(item) {
      const index = this.selectedTeams.findIndex((team) => team === item);
      if (index >= 0) this.selectedTeams.splice(index, 1);
    },
    getButtonSelectionState(selection) {
      return this.currentView === selection ? "primary" : "";
    },
    setButtonSelection(selection) {
      this.currentView = selection;
    },
    resetFilterCache() {
      this.selectedEmployees = [];
      this.selectedTeams = [];
      this.date.from = this.getDate();
      this.date.until = this.getDate();
      localStorage.removeItem("absenceReportFilter");
      localStorage.removeItem("absenceReportFromDate");
      localStorage.removeItem("absenceReportUntilDate");
      localStorage.removeItem("absenceReportEmployeesFilter");
      localStorage.removeItem("absenceReportTeamsFilter");
      this.fetch();
    }
  }
};
</script>

<style scoped>
.rotate {
  animation: rotation 2s infinite linear;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}

.report-scroll {
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.report-scroll > * {
  flex: 1 1 auto;
  min-height: 0;
}

.wrapper {
  z-index: 1000;
}

.page-size-wrapper {
  max-width: 140px;
}

.page-size-select .v-input__control {
  min-width: 0;
}

@media (max-width: 1264px) {
  .page-size-wrapper {
    max-width: 120px;
  }
}
</style>
